import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Building2, Boxes, CalendarClock, FileText, MapPin, Plus, Power, PowerOff, Printer, Receipt, Repeat, Trash2, Settings2, Users, Zap } from 'lucide-react';
import type { Alert, Customer, HealthState, Invoice, Resource, SessionUser, Site, Subscription, SubscriptionPlan, Ticket, UtilityBill } from '@itr/shared';
import { HEALTH_SEVERITY, HEALTH_STATES, UTILITY_BILL_CATEGORY_LABELS, accessHostFor, rollUpHealth, formatMoney, formatRelative, subscriptionLineTotal, utilityBillUrgency } from '@itr/shared';
import { api } from '../api/client';
import { movesHealthRollup, useRealtimeEvent, useThrottledReload } from '../api/realtime';
import { useAuth } from '../auth/AuthProvider';
import {
  Badge,
  Drawer,
  EmptyState,
  ErrorState,
  Health,
  RowCheckbox,
  SelectAllCheckbox,
  SelectionBar,
  SkeletonTable,
  autoIntent,
  bulkResultMessage,
  runBulk,
  useFetch,
  useSelection,
  useToast,
  type Selection,
  ReachDot,
} from '../components/ui';
import { ScopeOverview } from '../components/ScopeOverview';
import { DeviceFilterBar, useDeviceFilter, useResourceDescriber } from '../components/DeviceFilter';
import { IgnoreBulkButtons, IgnoredBadge } from '../components/ResourceHealth';
import { NetworkDiagram } from '../components/NetworkDiagram';
import { OneTimeChargesCard } from '../components/OneTimeCharges';
import { useLiveResources } from '../api/liveResources';
import { WatchToggle } from '../notifications/WatchToggle';
import { EditSubscriptionForm, SubscriptionForm } from './Invoices';
import { MarkUtilityBillPaidForm, UtilityBillForm } from './UtilityBills';
import { AddResourceForm } from './Resources';

type SubRow = Subscription & { planName: string; customerName: string; interval: string };
type InvoiceRow = Invoice & { customerName: string };
type BillRow = UtilityBill & { customerName: string; siteName: string | null };

const BILL_URGENCY_INTENT = {
  overdue: 'bad',
  due_soon: 'warn',
  ok: 'good',
  inactive: 'neutral',
} as const;

/** The list endpoints add tallies the bare records have no room for. */
export type ClientRow = Customer & { resourceCount: number; siteCount: number; health: Record<HealthState, number> };
export type SiteRow = Site & { resourceCount: number };

/* ================================================================== */
/* Clients                                                            */
/* ================================================================== */

export function ClientsPage() {
  const { can } = useAuth();
  const toast = useToast();
  const [addOpen, setAddOpen] = useState(false);
  const clients = useFetch(() => api.get<ClientRow[]>('/api/customers'), [], { refreshMs: 30_000 });
  // Live on every column: health (a resource's state flips or the set
  // changes — not every routine reading), site counts, and the client rows
  // themselves (added, renamed, deactivated, deleted, by anyone).
  const reloadClients = useThrottledReload(clients.reload, 3000);
  useRealtimeEvent(['resources', 'sites', 'customers'], (event) => {
    if (movesHealthRollup(event) || event.type === 'site.changed' || event.type === 'customer.changed') reloadClients();
  });

  const rows = clients.data ?? [];
  const totals = useMemo(
    () => ({
      sites: rows.reduce((n, c) => n + c.siteCount, 0),
      resources: rows.reduce((n, c) => n + c.resourceCount, 0),
      troubled: rows.filter((c) => (c.health?.down ?? 0) + (c.health?.degraded ?? 0) > 0).length,
    }),
    [rows],
  );

  const canWrite = can('user:manage');
  const selection = useSelection(rows.map((c) => c.id));

  const bulkSetActive = async (active: boolean) => {
    const result = await runBulk(selection.selectedIds, (id) => api.patch(`/api/customers/${id}`, { active }));
    const { message, intent } = bulkResultMessage(active ? 'Activated' : 'Deactivated', result);
    toast(message, intent);
    selection.clear();
    clients.reload();
  };

  return (
    <>
      <div className="page-header">
        <div className="grow">
          <h1 className="page-title">Clients</h1>
          <div className="page-subtitle">
            {rows.length} client{rows.length === 1 ? '' : 's'} · {totals.sites} site{totals.sites === 1 ? '' : 's'} ·{' '}
            {totals.resources} attached resource{totals.resources === 1 ? '' : 's'}
            {totals.troubled > 0 && (
              <>
                {' · '}
                <span style={{ color: 'var(--itr-warning)' }}>
                  {totals.troubled} client{totals.troubled === 1 ? '' : 's'} with problems
                </span>
              </>
            )}
          </div>
        </div>
        {can('user:manage') && (
          <button className="btn btn-primary" onClick={() => setAddOpen(true)}>
            <Plus size={15} /> Add client
          </button>
        )}
      </div>

      {canWrite && (
        <SelectionBar selection={selection}>
          <button className="btn btn-sm" onClick={() => bulkSetActive(true)}>
            <Power size={12} /> Activate
          </button>
          <button className="btn btn-sm" onClick={() => bulkSetActive(false)}>
            <PowerOff size={12} /> Deactivate
          </button>
        </SelectionBar>
      )}

      <div className="card">
        {clients.loading && !clients.data ? (
          <div style={{ padding: 16 }}>
            <SkeletonTable rows={5} cols={5} />
          </div>
        ) : clients.error ? (
          <ErrorState error={clients.error} onRetry={clients.reload} />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<Building2 size={20} />}
            title="No clients yet"
            text="A client is a tenant. Their sites, resources, tickets and billing are visible only to their own users."
            action={
              can('user:manage') ? (
                <button className="btn btn-primary" onClick={() => setAddOpen(true)}>
                  <Plus size={15} /> Add client
                </button>
              ) : undefined
            }
          />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  {canWrite && (
                    <th style={{ width: 40 }}>
                      <SelectAllCheckbox selection={selection} />
                    </th>
                  )}
                  <th>Client</th>
                  <th style={{ width: 90 }}>Code</th>
                  <th>Contact</th>
                  <th style={{ width: 90 }}>Sites</th>
                  <th style={{ width: 110 }}>Resources</th>
                  <th style={{ width: 220 }}>Health</th>
                  <th style={{ width: 90 }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((client) => (
                  <ClientRowView key={client.id} client={client} selection={canWrite ? selection : undefined} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Drawer open={addOpen} onClose={() => setAddOpen(false)} title="Add a client">
        <ClientForm
          onSaved={() => {
            setAddOpen(false);
            clients.reload();
            toast('Client created — add their sites next', 'good');
          }}
        />
      </Drawer>
    </>
  );
}

function ClientRowView({ client, selection }: { client: ClientRow; selection?: Selection }) {
  const navigate = useNavigate();
  return (
    <tr data-clickable="true" onClick={() => navigate(`/clients/${client.id}`)}>
      {selection && (
        <td>
          <RowCheckbox selection={selection} id={client.id} />
        </td>
      )}
      <td style={{ fontWeight: 550 }}>{client.name}</td>
      <td className="mono subtle">{client.code}</td>
      <td className="subtle truncate" style={{ maxWidth: 240, fontSize: 12.5 }}>
        {client.contactEmail ?? client.contactPhone ?? '—'}
      </td>
      <td className="subtle">{client.siteCount || '—'}</td>
      <td className="subtle">{client.resourceCount || '—'}</td>
      <td>
        <ClientHealth client={client} />
      </td>
      <td>
        <Badge intent={client.active ? 'good' : 'neutral'}>{client.active ? 'active' : 'inactive'}</Badge>
      </td>
    </tr>
  );
}

/** Short labels for the per-state tallies next to the rolled-up chip. */
const HEALTH_TALLY_LABEL: Record<HealthState, string> = {
  down: 'down',
  degraded: 'degraded',
  unknown: 'unknown',
  maintenance: 'maint.',
  up: 'up',
};

/**
 * One client's live status: the worst state across its resources, plus how
 * many resources sit in each state (worst first) so "1 down of 40" and
 * "everything down" don't look the same.
 */
function ClientHealth({ client }: { client: ClientRow }) {
  if (client.resourceCount === 0) return <span className="subtle">—</span>;
  const counts = client.health ?? ({} as Partial<Record<HealthState, number>>);
  const present = HEALTH_STATES.filter((h) => (counts[h] ?? 0) > 0).sort(
    (a, b) => HEALTH_SEVERITY[b] - HEALTH_SEVERITY[a],
  );
  const worst = rollUpHealth(present);
  const summary = present.map((h) => `${counts[h]} ${HEALTH_TALLY_LABEL[h]}`).join(' · ');
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}>
      <Health health={worst} reason={summary} />
      {worst !== 'up' && (
        <span className="subtle" style={{ fontSize: 12 }}>
          {summary}
        </span>
      )}
    </span>
  );
}

/* ================================================================== */
/* Client detail — the client's sites                                 */
/* ================================================================== */

export function ClientDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { can } = useAuth();
  const toast = useToast();
  const billingSectionRef = useRef<HTMLDivElement>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [addSiteOpen, setAddSiteOpen] = useState(false);
  const [addPlanOpen, setAddPlanOpen] = useState(false);
  const [editingSub, setEditingSub] = useState<SubRow | null>(null);
  const [addBillOpen, setAddBillOpen] = useState(false);
  const [editingBill, setEditingBill] = useState<BillRow | null>(null);
  const [payingBill, setPayingBill] = useState<BillRow | null>(null);

  const clients = useFetch(() => api.get<ClientRow[]>('/api/customers'), []);
  const sites = useFetch(() => api.get<SiteRow[]>('/api/sites', { customerId: id }), [id]);
  // Resources on this client that no site has claimed yet — the loose ends the
  // hierarchy is meant to surface.
  const unassigned = useFetch(
    () => api.get<Resource[]>('/api/resources', { customerId: id, limit: 300 }),
    [id],
  );
  const canBill = can('billing:read');
  const subs = useFetch(() => api.get<SubRow[]>('/api/billing/subscriptions', { customerId: id }), [id], { enabled: canBill });
  const plans = useFetch(() => api.get<SubscriptionPlan[]>('/api/billing/plans', { activeOnly: true }), [], { enabled: canBill });
  const invoices = useFetch(() => api.get<InvoiceRow[]>('/api/billing/invoices', { customerId: id, limit: 10 }), [id], { enabled: canBill });
  const bills = useFetch(() => api.get<BillRow[]>('/api/utility-bills', { customerId: id }), [id], { enabled: canBill });

  const client = (clients.data ?? []).find((c) => c.id === id);
  // Live: health patched in place, and the map, the site counts and the
  // unplaced list refetched when anything of this client's is added, moved,
  // deleted — here, in another tab, or by discovery.
  const clientResources = useLiveResources(
    unassigned.data,
    () => {
      unassigned.reload();
      sites.reload();
    },
    { customerId: id! },
  );
  const looseResources = clientResources.filter((r) => !r.siteId);
  const looseSelection = useSelection(looseResources.map((r) => r.id));

  // The "Billing" nav item sends a client-access role here with #billing —
  // everything else on this page (health widgets, network map, sites) loads
  // above the Subscriptions/Invoices/Utility bills cards, so without this a
  // client landing on their own overview page never sees why they came here.
  // Deferred a tick so it runs after the sections above have taken their
  // final height, not mid-layout.
  useEffect(() => {
    if (location.hash !== '#billing-section' || !client) return;
    const timer = setTimeout(() => billingSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
    return () => clearTimeout(timer);
  }, [location.hash, client]);

  // Sites are part of the resource hierarchy, not user management — a
  // client-access admin manages their own sites the same way they manage
  // their own resources.
  const canManage = can('resource:write');
  const siteSelection = useSelection((sites.data ?? []).map((s) => s.id));

  const bulkSetSiteActive = async (active: boolean) => {
    const result = await runBulk(siteSelection.selectedIds, (siteId) => api.patch(`/api/sites/${siteId}`, { active }));
    const { message, intent } = bulkResultMessage(active ? 'Activated' : 'Deactivated', result);
    toast(message, intent);
    siteSelection.clear();
    sites.reload();
  };

  const bulkDeleteSites = async () => {
    if (!confirm(`Delete ${siteSelection.count} site(s)? Their resources stay with the client, unplaced.`)) return;
    const result = await runBulk(siteSelection.selectedIds, (siteId) => api.delete(`/api/sites/${siteId}`));
    const { message, intent } = bulkResultMessage('Deleted', result);
    toast(message, intent);
    siteSelection.clear();
    sites.reload();
    clients.reload();
  };

  if (clients.loading && !clients.data) return <SkeletonTable rows={5} cols={3} />;
  if (clients.error) return <ErrorState error={clients.error} onRetry={clients.reload} />;
  if (!client) return <EmptyState title="Client not found" />;

  return (
    <div className="content-narrow" style={{ maxWidth: 1100 }}>
      <div className="page-header">
        <div className="grow">
          <div className="page-subtitle" style={{ marginBottom: 2 }}>
            <Link to="/clients">Clients</Link>
          </div>
          <div className="row" style={{ gap: 10 }}>
            <h1 className="page-title">{client.name}</h1>
            <Badge intent={client.active ? 'good' : 'neutral'}>{client.active ? 'active' : 'inactive'}</Badge>
          </div>
          <div className="page-subtitle">
            <span className="mono">{client.code}</span>
            {client.contactEmail && ` · ${client.contactEmail}`}
            {client.contactPhone && ` · ${client.contactPhone}`}
            {client.address && ` · ${client.address}`}
          </div>
        </div>
        <div className="row">
          <Link className="btn btn-sm" to={`/clients/${client.id}/report`}>
            <FileText size={14} /> Status report
          </Link>
          {can('user:manage') && (
            <button className="btn btn-icon" onClick={() => setEditOpen(true)} title="Edit client" aria-label="Edit client">
              <Settings2 size={15} />
            </button>
          )}
        </div>
      </div>

      <ScopeOverview endpoint={`/api/customers/${client.id}/overview`} />

      <NetworkDiagram
        client={client}
        sites={sites.data ?? []}
        resources={clientResources}
        layoutScope={`client:${client.id}`}
        onChanged={unassigned.reload}
      />

      {canBill && (
        <div className="card" style={{ marginBottom: 14 }} ref={billingSectionRef}>
          <div className="card-header">
            <Repeat size={14} />
            <span className="card-title">Subscriptions</span>
            <span className="grow" />
            {can('billing:write') && (
              <button className="btn btn-sm btn-primary" onClick={() => setAddPlanOpen(true)}>
                <Plus size={13} /> Add plan
              </button>
            )}
          </div>
          {subs.loading && !subs.data ? (
            <div style={{ padding: 16 }}>
              <SkeletonTable rows={2} cols={4} />
            </div>
          ) : (subs.data ?? []).length === 0 ? (
            <EmptyState
              icon={<Repeat size={20} />}
              title="No plan yet"
              text="This client is not on a recurring plan."
              action={
                can('billing:write') ? (
                  <button className="btn btn-primary" onClick={() => setAddPlanOpen(true)}>
                    <Plus size={15} /> Add plan
                  </button>
                ) : undefined
              }
            />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Plan</th>
                    <th data-align="right" style={{ width: 70 }}>
                      Qty
                    </th>
                    <th data-align="right" style={{ width: 100 }}>
                      Price
                    </th>
                    <th style={{ width: 100 }}>Status</th>
                    <th style={{ width: 130 }}>Renews</th>
                    {can('billing:write') && <th style={{ width: 70 }} />}
                  </tr>
                </thead>
                <tbody>
                  {(subs.data ?? []).map((sub) => {
                    const plan = (plans.data ?? []).find((p) => p.id === sub.planId);
                    return (
                      <tr key={sub.id}>
                        <td style={{ fontWeight: 550 }}>
                          {sub.planName}
                          <span className="subtle" style={{ fontSize: 11.5, marginLeft: 6 }}>
                            {sub.interval}
                          </span>
                        </td>
                        <td data-align="right" className="nums">
                          {sub.quantity}
                        </td>
                        <td data-align="right" className="nums subtle">
                          {plan ? formatMoney(subscriptionLineTotal(sub, plan), plan.currency) : '—'}
                        </td>
                        <td>
                          <Badge intent={autoIntent(sub.status)}>{sub.status}</Badge>
                        </td>
                        <td className="subtle" style={{ fontSize: 12 }}>
                          {sub.cancelAt ? `ends ${new Date(sub.cancelAt).toISOString().slice(0, 10)}` : formatRelative(sub.periodEnd)}
                        </td>
                        {can('billing:write') && (
                          <td>
                            <button className="btn btn-sm" onClick={() => setEditingSub(sub)}>
                              Edit
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {canBill && <OneTimeChargesCard customerId={client.id} />}

      {canBill && (
        <div className="card" style={{ marginBottom: 14 }}>
          <div className="card-header">
            <Receipt size={14} />
            <span className="card-title">Our invoices</span>
            <span className="grow" />
            <Link className="btn btn-sm" to="/invoices">
              All invoices
            </Link>
          </div>
          {invoices.loading && !invoices.data ? (
            <div style={{ padding: 16 }}>
              <SkeletonTable rows={2} cols={4} />
            </div>
          ) : (invoices.data ?? []).length === 0 ? (
            <EmptyState icon={<Receipt size={20} />} title="No invoices yet" text="Nothing has been billed to this client for our service plan yet." />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th style={{ width: 140 }}>Number</th>
                    <th style={{ width: 180 }}>Period</th>
                    <th data-align="right" style={{ width: 110 }}>
                      Total
                    </th>
                    <th style={{ width: 100 }}>State</th>
                    <th data-align="right" style={{ width: 120 }}>
                      Due
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(invoices.data ?? []).map((inv) => (
                    <tr key={inv.id} data-clickable="true" onClick={() => navigate(`/invoices/${inv.id}`)}>
                      <td className="mono">{inv.number}</td>
                      <td className="subtle" style={{ fontSize: 12 }}>
                        {isoDate(inv.periodStart)} → {isoDate(inv.periodEnd)}
                      </td>
                      <td data-align="right" className="nums" style={{ fontWeight: 600 }}>
                        {formatMoney(inv.totalMinor, inv.currency)}
                      </td>
                      <td>
                        <Badge intent={autoIntent(inv.state)}>{inv.state}</Badge>
                      </td>
                      <td data-align="right" className="subtle" style={{ fontSize: 12 }}>
                        {inv.dueAt ? formatRelative(inv.dueAt) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {canBill && (
        <div className="card" style={{ marginBottom: 14 }}>
          <div className="card-header">
            <CalendarClock size={14} />
            <span className="card-title">Utility bills & alerts</span>
            <span className="grow" />
            {can('utility:write') && (
              <button className="btn btn-sm btn-primary" onClick={() => setAddBillOpen(true)}>
                <Plus size={13} /> Add bill
              </button>
            )}
          </div>
          {bills.loading && !bills.data ? (
            <div style={{ padding: 16 }}>
              <SkeletonTable rows={2} cols={5} />
            </div>
          ) : (bills.data ?? []).length === 0 ? (
            <EmptyState
              icon={<Zap size={20} />}
              title="No utility bills tracked"
              text="Add this client's internet, power, phone or leased-line bill to get reminders before a lapse could interrupt their call center."
              action={
                can('utility:write') ? (
                  <button className="btn btn-primary" onClick={() => setAddBillOpen(true)}>
                    <Plus size={15} /> Add bill
                  </button>
                ) : undefined
              }
            />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Provider</th>
                    <th data-align="right" style={{ width: 100 }}>
                      Amount
                    </th>
                    <th style={{ width: 150 }}>Next due</th>
                    <th style={{ width: 90 }}>Cycle</th>
                    {can('utility:write') && <th style={{ width: 150 }} />}
                  </tr>
                </thead>
                <tbody>
                  {(bills.data ?? []).map((bill) => {
                    const urgency = utilityBillUrgency(bill);
                    return (
                      <tr key={bill.id}>
                        <td style={{ fontWeight: 550 }}>
                          {UTILITY_BILL_CATEGORY_LABELS[bill.category]}
                          {bill.label && (
                            <span className="subtle" style={{ fontSize: 11.5, marginLeft: 6 }}>
                              {bill.label}
                            </span>
                          )}
                        </td>
                        <td className="subtle">{bill.provider || '—'}</td>
                        <td data-align="right" className="nums">
                          {formatMoney(bill.amountMinor, bill.currency)}
                        </td>
                        <td>
                          <div className="row" style={{ gap: 6 }}>
                            <Badge intent={BILL_URGENCY_INTENT[urgency]}>{urgency.replace('_', ' ')}</Badge>
                            <span className="subtle" style={{ fontSize: 12 }}>
                              {formatRelative(bill.nextDueAt)}
                            </span>
                          </div>
                        </td>
                        <td className="subtle" style={{ fontSize: 12 }}>
                          {bill.cycle.replace('_', ' ')}
                        </td>
                        {can('utility:write') && (
                          <td>
                            <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                              {bill.active && (
                                <button className="btn btn-sm" onClick={() => setPayingBill(bill)}>
                                  Mark paid
                                </button>
                              )}
                              <button className="btn btn-sm" onClick={() => setEditingBill(bill)}>
                                Edit
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <ClientTeamCard />

      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-header">
          <span className="card-title">Sites</span>
          <span className="grow" />
          {can('resource:write') && (
            <button className="btn btn-sm btn-primary" onClick={() => setAddSiteOpen(true)}>
              <MapPin size={13} /> Add site
            </button>
          )}
        </div>
        {canManage && (
          <div style={{ padding: '0 14px' }}>
            <SelectionBar selection={siteSelection}>
              <button className="btn btn-sm" onClick={() => bulkSetSiteActive(true)}>
                <Power size={12} /> Activate
              </button>
              <button className="btn btn-sm" onClick={() => bulkSetSiteActive(false)}>
                <PowerOff size={12} /> Deactivate
              </button>
              <button className="btn btn-sm btn-danger" onClick={bulkDeleteSites}>
                <Trash2 size={12} /> Delete
              </button>
            </SelectionBar>
          </div>
        )}
        {sites.loading && !sites.data ? (
          <div style={{ padding: 16 }}>
            <SkeletonTable rows={3} cols={4} />
          </div>
        ) : (sites.data ?? []).length === 0 ? (
          <EmptyState
            icon={<MapPin size={20} />}
            title="No sites yet"
            text="A site is a location — head office, a branch, a data centre. Resources attach to a site."
            action={
              can('resource:write') ? (
                <button className="btn btn-primary" onClick={() => setAddSiteOpen(true)}>
                  <MapPin size={15} /> Add site
                </button>
              ) : undefined
            }
          />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  {canManage && (
                    <th style={{ width: 40 }}>
                      <SelectAllCheckbox selection={siteSelection} />
                    </th>
                  )}
                  <th>Site</th>
                  <th style={{ width: 90 }}>Code</th>
                  <th>Address</th>
                  <th style={{ width: 110 }}>Resources</th>
                  <th style={{ width: 90 }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {(sites.data ?? []).map((site) => (
                  <SiteRowView key={site.id} site={site} clientId={client.id} selection={canManage ? siteSelection : undefined} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <div className="card-header">
          <span className="card-title">Not yet at a site</span>
          <span className="grow" />
          <Link className="btn btn-sm" to={`/resources?customerId=${client.id}`}>
            <Boxes size={13} /> All their resources
          </Link>
        </div>
        {looseResources.length === 0 ? (
          <EmptyState title="Every resource is placed" text="All of this client's resources belong to one of their sites." />
        ) : (
          <div className="table-wrap">
            {can('resource:write') && (
              <div style={{ padding: '10px var(--itr-pad) 0' }}>
                <SelectionBar selection={looseSelection}>
                  <IgnoreBulkButtons selection={looseSelection} onChanged={unassigned.reload} />
                </SelectionBar>
              </div>
            )}
            <table className="table">
              <thead>
                <tr>
                  {can('resource:write') && (
                    <th style={{ width: 36 }}>
                      <SelectAllCheckbox selection={looseSelection} />
                    </th>
                  )}
                  <th>Name</th>
                  <th style={{ width: 110 }}>Health</th>
                  <th style={{ width: 130 }}>Kind</th>
                  <th style={{ width: 140 }}>Address</th>
                </tr>
              </thead>
              <tbody>
                {looseResources.map((resource) => (
                  <tr key={resource.id}>
                    {can('resource:write') && (
                      <td>
                        <RowCheckbox selection={looseSelection} id={resource.id} />
                      </td>
                    )}
                    <td style={{ fontWeight: 550 }}>
                      <Link to={`/resources/${resource.id}`}>
                        <ReachDot health={resource.health} />
                        {resource.name}
                      </Link>
                    </td>
                    <td>
                      <Health health={resource.health} reason={resource.statusMessage} />
                      <IgnoredBadge resource={resource} />
                    </td>
                    <td>
                      <Badge>{resource.kind}</Badge>
                    </td>
                    <td className="mono subtle">{accessHostFor(resource) ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Drawer open={editOpen} onClose={() => setEditOpen(false)} title={client.name}>
        <ClientForm
          client={client}
          onSaved={() => {
            setEditOpen(false);
            clients.reload();
            toast('Client updated', 'good');
          }}
        />
      </Drawer>

      <Drawer open={addSiteOpen} onClose={() => setAddSiteOpen(false)} title={`Add a site for ${client.name}`}>
        <SiteForm
          customerId={client.id}
          onSaved={() => {
            setAddSiteOpen(false);
            sites.reload();
            clients.reload();
            toast('Site created', 'good');
          }}
        />
      </Drawer>

      <Drawer open={addPlanOpen} onClose={() => setAddPlanOpen(false)} title={`Add a plan for ${client.name}`}>
        <SubscriptionForm
          plans={plans.data ?? []}
          customers={[]}
          fixedCustomerId={client.id}
          onSaved={() => {
            setAddPlanOpen(false);
            subs.reload();
            toast('Plan added', 'good');
          }}
        />
      </Drawer>

      <Drawer open={Boolean(editingSub)} onClose={() => setEditingSub(null)} title={editingSub ? `Edit — ${editingSub.planName}` : ''}>
        {editingSub && (
          <EditSubscriptionForm
            subscription={editingSub}
            onSaved={() => {
              setEditingSub(null);
              subs.reload();
              toast('Subscription updated', 'good');
            }}
          />
        )}
      </Drawer>

      <Drawer open={addBillOpen} onClose={() => setAddBillOpen(false)} title={`Add a utility bill for ${client.name}`}>
        <UtilityBillForm
          customers={[]}
          fixedCustomerId={client.id}
          onSaved={() => {
            setAddBillOpen(false);
            bills.reload();
            toast('Utility bill added', 'good');
          }}
        />
      </Drawer>

      <Drawer open={Boolean(editingBill)} onClose={() => setEditingBill(null)} title={editingBill ? `Edit — ${UTILITY_BILL_CATEGORY_LABELS[editingBill.category]}` : ''}>
        {editingBill && (
          <UtilityBillForm
            bill={editingBill}
            customers={[]}
            fixedCustomerId={client.id}
            onSaved={() => {
              setEditingBill(null);
              bills.reload();
              toast('Utility bill updated', 'good');
            }}
          />
        )}
      </Drawer>

      <Drawer open={Boolean(payingBill)} onClose={() => setPayingBill(null)} title={payingBill ? `Mark paid — ${UTILITY_BILL_CATEGORY_LABELS[payingBill.category]}` : ''}>
        {payingBill && (
          <MarkUtilityBillPaidForm
            bill={payingBill}
            onSaved={() => {
              setPayingBill(null);
              bills.reload();
              toast('Payment recorded', 'good');
            }}
          />
        )}
      </Drawer>
    </div>
  );
}

/**
 * Only ever renders for a `client_user:manage` holder looking at their own
 * account — the endpoint has no `customerId` param, it always resolves to
 * the caller's own tenant, so there's nothing here for staff to reach into.
 */
function ClientTeamCard() {
  const { can, user } = useAuth();
  const toast = useToast();
  const [inviteOpen, setInviteOpen] = useState(false);
  const canManageTeam = can('client_user:manage');
  const team = useFetch(() => api.get<SessionUser[]>('/api/client-team'), [], { enabled: canManageTeam });

  if (!canManageTeam) return null;

  const removeMember = async (memberId: string) => {
    if (!confirm('Remove this teammate? They will lose access immediately.')) return;
    try {
      await api.delete(`/api/client-team/${memberId}`);
      toast('Teammate removed', 'good');
      team.reload();
    } catch (err) {
      toast('Could not remove teammate', 'bad', err instanceof Error ? err.message : undefined);
    }
  };

  const changeRole = async (memberId: string, role: string) => {
    try {
      await api.patch(`/api/client-team/${memberId}`, { role });
      toast('Role updated', 'good');
      team.reload();
    } catch (err) {
      toast('Could not update role', 'bad', err instanceof Error ? err.message : undefined);
    }
  };

  return (
    <div className="card" style={{ marginBottom: 14 }}>
      <div className="card-header">
        <Users size={14} />
        <span className="card-title">Team</span>
        <span className="grow" />
        <button className="btn btn-sm btn-primary" onClick={() => setInviteOpen(true)}>
          <Plus size={13} /> Add teammate
        </button>
      </div>
      {team.loading && !team.data ? (
        <div style={{ padding: 16 }}>
          <SkeletonTable rows={2} cols={3} />
        </div>
      ) : (team.data ?? []).length === 0 ? (
        <EmptyState icon={<Users size={20} />} title="No teammates yet" text="Add people at this client so they can view or manage their own account." />
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th style={{ width: 130 }}>Role</th>
                <th style={{ width: 60 }} />
              </tr>
            </thead>
            <tbody>
              {(team.data ?? []).map((member) => (
                <tr key={member.id}>
                  <td style={{ fontWeight: 550 }}>{member.name}</td>
                  <td className="subtle">{member.email}</td>
                  <td>
                    <select
                      className="select"
                      value={member.role}
                      disabled={member.id === user?.id}
                      title={member.id === user?.id ? "You can't change your own role here" : undefined}
                      onChange={(e) => changeRole(member.id, e.target.value)}
                    >
                      <option value="customer">Viewer</option>
                      <option value="customer_admin">Admin</option>
                    </select>
                  </td>
                  <td>
                    {member.id !== user?.id && (
                      <button className="btn btn-sm" onClick={() => removeMember(member.id)} title="Remove" aria-label="Remove">
                        <Trash2 size={12} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Drawer open={inviteOpen} onClose={() => setInviteOpen(false)} title="Add a teammate">
        <InviteTeammateForm
          onSaved={() => {
            setInviteOpen(false);
            team.reload();
          }}
        />
      </Drawer>
    </div>
  );
}

function InviteTeammateForm({ onSaved }: { onSaved: () => void }) {
  const toast = useToast();
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'customer', phone: '' });
  const [busy, setBusy] = useState(false);

  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/api/client-team', {
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password,
        role: form.role,
        phone: form.phone || null,
      });
      toast('Teammate added', 'good');
      onSaved();
    } catch (err) {
      toast('Could not add teammate', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <div className="field">
        <label className="field-label">Name</label>
        <input className="input" required autoFocus value={form.name} onChange={set('name')} />
      </div>
      <div className="field">
        <label className="field-label">Email</label>
        <input className="input" type="email" required value={form.email} onChange={set('email')} />
      </div>
      <div className="field">
        <label className="field-label">Temporary password</label>
        <input className="input" type="password" required minLength={8} value={form.password} onChange={set('password')} autoComplete="new-password" />
      </div>
      <div className="field">
        <label className="field-label">Role</label>
        <select className="select" value={form.role} onChange={set('role')}>
          <option value="customer">Viewer</option>
          <option value="customer_admin">Admin</option>
        </select>
      </div>
      <div className="field">
        <label className="field-label">Phone (optional)</label>
        <input className="input" value={form.phone} onChange={set('phone')} />
      </div>
      <button className="btn btn-primary" type="submit" disabled={busy}>
        {busy ? 'Adding…' : 'Add teammate'}
      </button>
    </form>
  );
}

function isoDate(ts: number): string {
  return new Date(ts).toISOString().slice(0, 10);
}

/* ================================================================== */
/* Status report — printable/exportable snapshot for one client       */
/* ================================================================== */

interface ReportOverview {
  resourceCount: number;
  health: Record<HealthState, number>;
  alerts: { critical: number; warning: number; info: number; acknowledged: number };
  openAlerts: Alert[];
  tickets: { open: number; unassigned: number; breached: number };
  openTickets: Ticket[];
  uptimeNow: number;
  uptime24h: number;
  uptime7d: number;
  uptime30d: number;
}

interface ReportResourceRow {
  id: string;
  name: string;
  kind: string;
  pluginId: string;
  health: HealthState;
  statusMessage: string | null;
  uptimePct: number;
  lastCheckedAt: number | null;
}

interface ReportData {
  customer: Customer;
  generatedAt: number;
  periodFrom: number;
  periodTo: number;
  overview: ReportOverview;
  resources: ReportResourceRow[];
}

const REPORT_RANGES = [
  { hours: 24, label: '24 hours' },
  { hours: 24 * 7, label: '7 days' },
  { hours: 24 * 30, label: '30 days' },
  { hours: 24 * 90, label: '90 days' },
];

/**
 * A frozen, printable status report — everything the live dashboard shows,
 * laid out for a client who wants a document, not a browser tab. Printing
 * uses the browser's native "Save as PDF" rather than a server-side renderer:
 * one fewer dependency, and the result is selectable/searchable text rather
 * than a rasterised page.
 */
export function ReportPage() {
  const { id } = useParams<{ id: string }>();
  const [hours, setHours] = useState(24 * 30);
  const { data, loading, error, reload } = useFetch(
    () => api.get<ReportData>(`/api/customers/${id}/report`, { hours }),
    [id, hours],
  );

  if (loading && !data) return <SkeletonTable rows={8} cols={4} />;
  if (error) return <ErrorState error={error} onRetry={reload} />;
  if (!data) return null;

  const { customer, overview, resources } = data;
  const period = REPORT_RANGES.find((r) => r.hours === hours)?.label ?? `${hours}h`;

  return (
    <div className="content-narrow" style={{ maxWidth: 1000 }}>
      <div className="page-header">
        <div className="grow">
          <div className="page-subtitle no-print" style={{ marginBottom: 2 }}>
            <Link to={`/clients/${customer.id}`}>{customer.name}</Link>
          </div>
          <div className="row" style={{ gap: 10, alignItems: 'baseline' }}>
            <h1 className="page-title">{customer.name} — status report</h1>
            <span className="subtle mono" style={{ fontSize: 12 }}>
              {customer.code}
            </span>
          </div>
          <div className="page-subtitle">
            {period} · generated {new Date(data.generatedAt).toLocaleString()}
            {customer.address && ` · ${customer.address}`}
          </div>
        </div>
        <div className="row no-print" style={{ gap: 4 }}>
          {REPORT_RANGES.map((r) => (
            <button
              key={r.hours}
              className={`btn btn-sm${hours === r.hours ? ' btn-primary' : ''}`}
              onClick={() => setHours(r.hours)}
            >
              {r.label}
            </button>
          ))}
          <button className="btn btn-primary" onClick={() => window.print()}>
            <Printer size={14} /> Print / Save PDF
          </button>
        </div>
      </div>

      <div className="stat-strip" style={{ marginBottom: 16 }}>
        <div className="stat-chip">
          <div className="stat-chip-label">Resources</div>
          <div className="stat-chip-value">{overview.resourceCount}</div>
        </div>
        <div className="stat-chip">
          <div className="stat-chip-label">Up now</div>
          <div className="stat-chip-value">
            {overview.health.up ?? 0}/{overview.resourceCount}
          </div>
        </div>
        <div className="stat-chip">
          <div className="stat-chip-label">Uptime 24h</div>
          <div className="stat-chip-value">{overview.uptime24h.toFixed(2)}%</div>
        </div>
        <div className="stat-chip">
          <div className="stat-chip-label">Uptime 30d</div>
          <div className="stat-chip-value">{overview.uptime30d.toFixed(2)}%</div>
        </div>
        <div className="stat-chip">
          <div className="stat-chip-label">Alerts this period</div>
          <div className="stat-chip-value">{overview.alerts.critical + overview.alerts.warning + overview.alerts.info}</div>
        </div>
        <div className="stat-chip">
          <div className="stat-chip-label">Open tickets</div>
          <div className="stat-chip-value">{overview.tickets.open}</div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header">
          <span className="card-title">Resources ({resources.length})</span>
        </div>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Kind</th>
                <th style={{ width: 100 }}>Health</th>
                <th data-align="right" style={{ width: 90 }}>
                  Uptime
                </th>
                <th style={{ width: 140 }}>Last checked</th>
              </tr>
            </thead>
            <tbody>
              {resources.map((r) => (
                <tr key={r.id}>
                  <td style={{ fontWeight: 550 }}>{r.name}</td>
                  <td className="subtle">{r.kind}</td>
                  <td>
                    <Health health={r.health} reason={r.statusMessage} />
                  </td>
                  <td data-align="right" className="nums">
                    {r.uptimePct.toFixed(2)}%
                  </td>
                  <td className="subtle" style={{ fontSize: 12 }}>
                    {r.lastCheckedAt ? formatRelative(r.lastCheckedAt) : 'never'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {overview.openAlerts.length > 0 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header">
            <span className="card-title">Open alerts ({overview.openAlerts.length})</span>
          </div>
          <div style={{ padding: '4px 0' }}>
            {overview.openAlerts.map((alert) => (
              <div key={alert.id} className="row" style={{ padding: '9px 16px', gap: 10 }}>
                <Badge intent={alert.severity === 'critical' ? 'bad' : 'warn'}>{alert.severity}</Badge>
                <span className="grow truncate">{alert.title}</span>
                <span className="subtle" style={{ fontSize: 12 }}>
                  {formatRelative(alert.lastSeenAt)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {overview.openTickets.length > 0 && (
        <div className="card">
          <div className="card-header">
            <span className="card-title">Open tickets ({overview.openTickets.length})</span>
          </div>
          <div style={{ padding: '4px 0' }}>
            {overview.openTickets.map((ticket) => (
              <div key={ticket.id} className="row" style={{ padding: '9px 16px', gap: 10 }}>
                <Badge intent={ticket.priority === 'urgent' || ticket.priority === 'high' ? 'bad' : 'neutral'}>{ticket.priority}</Badge>
                <span className="grow truncate">{ticket.subject}</span>
                <span className="subtle" style={{ fontSize: 12 }}>
                  {formatRelative(ticket.updatedAt)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SiteRowView({ site, clientId, selection }: { site: SiteRow; clientId: string; selection?: Selection }) {
  const navigate = useNavigate();
  return (
    <tr data-clickable="true" onClick={() => navigate(`/clients/${clientId}/sites/${site.id}`)}>
      {selection && (
        <td>
          <RowCheckbox selection={selection} id={site.id} />
        </td>
      )}
      <td style={{ fontWeight: 550 }}>{site.name}</td>
      <td className="mono subtle">{site.code ?? '—'}</td>
      <td className="subtle truncate" style={{ maxWidth: 260, fontSize: 12.5 }}>
        {site.address ?? '—'}
      </td>
      <td className="subtle">{site.resourceCount || '—'}</td>
      <td>
        <Badge intent={site.active ? 'good' : 'neutral'}>{site.active ? 'active' : 'inactive'}</Badge>
      </td>
    </tr>
  );
}

/* ================================================================== */
/* Site detail — the resources at that site                           */
/* ================================================================== */

export function SiteDetail() {
  const { id: clientId, siteId } = useParams<{ id: string; siteId: string }>();
  const { can } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [editOpen, setEditOpen] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  const site = useFetch(() => api.get<SiteRow>(`/api/sites/${siteId}`), [siteId]);
  const clients = useFetch(() => api.get<ClientRow[]>('/api/customers'), []);
  const resources = useFetch(() => api.get<Resource[]>('/api/resources', { siteId, limit: 300 }), [siteId], {
    refreshMs: 30_000,
  });

  const client = (clients.data ?? []).find((c) => c.id === clientId);
  // Scoped to the client, not the site: a resource moved *into* this site
  // from elsewhere at the client isn't in the list yet to be matched by id.
  const rows = useLiveResources(
    resources.data,
    () => {
      resources.reload();
      site.reload();
    },
    { customerId: clientId! },
  );
  // The table below filters by type, status, name, IP or MAC prefix; the map
  // above has its own copy of the same filter, so each is narrowed separately.
  const describe = useResourceDescriber();
  const filter = useDeviceFilter(rows, describe);
  const rowSelection = useSelection(filter.visible.map((r) => r.id));

  const detach = async (resource: Resource) => {
    if (!confirm(`Remove "${resource.name}" from this site? It stays with the client.`)) return;
    await api.patch(`/api/resources/${resource.id}`, { siteId: null });
    resources.reload();
    site.reload();
    toast('Resource detached from the site', 'good');
  };

  if (site.loading && !site.data) return <SkeletonTable rows={5} cols={4} />;
  if (site.error) return <ErrorState error={site.error} onRetry={site.reload} />;
  if (!site.data) return <EmptyState title="Site not found" />;

  return (
    <div className="content-narrow" style={{ maxWidth: 1100 }}>
      <div className="page-header">
        <div className="grow">
          <div className="page-subtitle" style={{ marginBottom: 2 }}>
            <Link to="/clients">Clients</Link>
            {' / '}
            <Link to={`/clients/${clientId}`}>{client?.name ?? 'Client'}</Link>
          </div>
          <div className="row" style={{ gap: 10 }}>
            <h1 className="page-title">{site.data.name}</h1>
            <Badge intent={site.data.active ? 'good' : 'neutral'}>{site.data.active ? 'active' : 'inactive'}</Badge>
          </div>
          <div className="page-subtitle">
            {site.data.code && <span className="mono">{site.data.code}</span>}
            {site.data.address && `${site.data.code ? ' · ' : ''}${site.data.address}`}
            {site.data.contactName && ` · ${site.data.contactName}`}
            {site.data.contactPhone && ` · ${site.data.contactPhone}`}
          </div>
        </div>
        <div className="row">
          <WatchToggle scope="site" id={site.data.id} name={site.data.name} />
          {can('resource:write') && (
            <>
              <button className="btn" onClick={() => setAddOpen(true)}>
                <Plus size={15} /> Add resource
              </button>
              <button className="btn btn-primary" onClick={() => setAttachOpen(true)}>
                <Plus size={15} /> Attach resources
              </button>
            </>
          )}
          {can('resource:write') && (
            <>
              <button className="btn btn-icon" onClick={() => setEditOpen(true)} title="Edit site" aria-label="Edit site">
                <Settings2 size={15} />
              </button>
              <button
                className="btn btn-icon"
                title="Delete site"
                aria-label="Delete site"
                onClick={async () => {
                  if (!confirm(`Delete "${site.data!.name}"? Its resources stay with the client, unplaced.`)) return;
                  await api.delete(`/api/sites/${siteId}`);
                  toast('Site deleted', 'good');
                  navigate(`/clients/${clientId}`);
                }}
              >
                <Trash2 size={15} />
              </button>
            </>
          )}
        </div>
      </div>

      {site.data.notes && (
        <div className="card" style={{ padding: '10px 14px', marginBottom: 14, fontSize: 13 }}>{site.data.notes}</div>
      )}

      <ScopeOverview endpoint={`/api/sites/${siteId}/overview`} />

      {client && (
        <NetworkDiagram client={client} sites={[site.data]} resources={rows} layoutScope={`site:${site.data.id}`} onChanged={resources.reload} />
      )}

      <div className="card">
        <div className="card-header">
          <span className="card-title">Resources at this site</span>
          <span className="grow" />
          <span className="subtle" style={{ fontSize: 12.5 }}>
            {filter.active ? `${filter.visible.length} of ${rows.length}` : rows.length} attached
          </span>
        </div>
        {rows.length > 0 && (
          <div style={{ paddingBottom: 10 }}>
            <DeviceFilterBar filter={filter} total={rows.length} />
            {can('resource:write') && (
              <div style={{ padding: '10px var(--itr-pad) 0' }}>
                <SelectionBar selection={rowSelection}>
                  <IgnoreBulkButtons selection={rowSelection} onChanged={resources.reload} />
                </SelectionBar>
              </div>
            )}
          </div>
        )}
        {rows.length === 0 ? (
          <EmptyState
            icon={<Boxes size={20} />}
            title="Nothing here yet"
            text="Attach the firewalls, switches, servers and lines that live at this location."
            action={
              can('resource:write') ? (
                <div className="row" style={{ gap: 8 }}>
                  <button className="btn" onClick={() => setAddOpen(true)}>
                    <Plus size={15} /> Add resource
                  </button>
                  <button className="btn btn-primary" onClick={() => setAttachOpen(true)}>
                    <Plus size={15} /> Attach resources
                  </button>
                </div>
              ) : undefined
            }
          />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  {can('resource:write') && (
                    <th style={{ width: 36 }}>
                      <SelectAllCheckbox selection={rowSelection} />
                    </th>
                  )}
                  <th>Name</th>
                  <th style={{ width: 110 }}>Health</th>
                  <th style={{ width: 130 }}>Kind</th>
                  <th>Status</th>
                  <th style={{ width: 130 }}>Address</th>
                  <th data-align="right" style={{ width: 120 }}>
                    Last check
                  </th>
                  <th style={{ width: 44 }} />
                </tr>
              </thead>
              <tbody>
                {filter.active && filter.visible.length === 0 && (
                  <tr>
                    <td colSpan={8} className="subtle" style={{ textAlign: 'center', fontSize: 12.5 }}>
                      No resources match the filter.
                    </td>
                  </tr>
                )}
                {filter.visible.map((resource) => (
                  <tr key={resource.id}>
                    {can('resource:write') && (
                      <td>
                        <RowCheckbox selection={rowSelection} id={resource.id} />
                      </td>
                    )}
                    <td style={{ fontWeight: 550 }}>
                      <Link to={`/resources/${resource.id}`}>
                        <ReachDot health={resource.health} />
                        {resource.name}
                      </Link>
                    </td>
                    <td>
                      <Health health={resource.health} reason={resource.statusMessage} />
                      <IgnoredBadge resource={resource} />
                    </td>
                    <td>
                      <Badge>{resource.kind}</Badge>
                    </td>
                    <td className="subtle truncate" style={{ maxWidth: 280, fontSize: 12.5 }}>
                      {resource.statusMessage ?? '—'}
                    </td>
                    <td className="mono subtle">{accessHostFor(resource) ?? '—'}</td>
                    <td data-align="right" className="subtle" style={{ fontSize: 12 }}>
                      {resource.lastCheckedAt ? formatRelative(resource.lastCheckedAt) : 'never'}
                    </td>
                    <td>
                      {can('resource:write') && (
                        <button
                          className="btn btn-ghost btn-icon btn-sm"
                          onClick={() => void detach(resource)}
                          title="Detach from site"
                          aria-label="Detach from site"
                        >
                          ×
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Drawer open={editOpen} onClose={() => setEditOpen(false)} title={site.data.name}>
        <SiteForm
          customerId={site.data.customerId}
          site={site.data}
          onSaved={() => {
            setEditOpen(false);
            site.reload();
            toast('Site updated', 'good');
          }}
        />
      </Drawer>

      <Drawer open={attachOpen} onClose={() => setAttachOpen(false)} title={`Attach resources to ${site.data.name}`}>
        <AttachResourcesForm
          site={site.data}
          customers={clients.data ?? []}
          onSaved={() => {
            setAttachOpen(false);
            resources.reload();
            site.reload();
            toast('Resources attached', 'good');
          }}
        />
      </Drawer>

      <Drawer open={addOpen} onClose={() => setAddOpen(false)} title={`Add a resource to ${site.data.name}`}>
        <AddResourceForm
          lockPlacement={{ customerId: site.data.customerId, siteId: site.data.id, label: site.data.name }}
          onCreated={() => {
            setAddOpen(false);
            resources.reload();
            site.reload();
            toast('Resource added — first check runs shortly', 'good');
          }}
        />
      </Drawer>
    </div>
  );
}

/* ================================================================== */
/* Forms                                                              */
/* ================================================================== */

/** One form for both create and edit — the fields are identical. */
export function ClientForm({ client, onSaved }: { client?: Customer; onSaved: () => void }) {
  const toast = useToast();
  const [form, setForm] = useState({
    name: client?.name ?? '',
    code: client?.code ?? '',
    contactEmail: client?.contactEmail ?? '',
    contactPhone: client?.contactPhone ?? '',
    address: client?.address ?? '',
  });
  const [active, setActive] = useState(client?.active ?? true);
  const [busy, setBusy] = useState(false);

  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [key]: key === 'code' ? e.target.value.toUpperCase() : e.target.value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        code: form.code.trim(),
        // The server validates `contactEmail` as an email, so send null rather
        // than an empty string when it was left blank.
        contactEmail: form.contactEmail.trim() || null,
        contactPhone: form.contactPhone.trim() || null,
        address: form.address.trim() || null,
      };
      if (client) await api.patch(`/api/customers/${client.id}`, { ...payload, active });
      else await api.post('/api/customers', payload);
      onSaved();
    } catch (err) {
      toast(client ? 'Could not update the client' : 'Could not create the client', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit}>
      {!client && (
        <p className="subtle" style={{ fontSize: 13, marginBottom: 16 }}>
          A client is a tenant. Their sites, resources, tickets, credentials and billing are visible only to their own users —
          enforced in the data layer, not the UI.
        </p>
      )}
      <div className="field">
        <label className="field-label">Name</label>
        <input className="input" required autoFocus value={form.name} onChange={set('name')} placeholder="Northwind Trading" />
      </div>
      <div className="field">
        <label className="field-label">Code</label>
        <input className="input mono" required value={form.code} onChange={set('code')} placeholder="NWT" />
        <span className="field-help">Short tenant identifier used on tickets, invoices and billing accounts.</span>
      </div>
      <div className="field">
        <label className="field-label">Contact email</label>
        <input className="input" type="email" value={form.contactEmail} onChange={set('contactEmail')} placeholder="it@example.com" />
      </div>
      <div className="field">
        <label className="field-label">Contact phone</label>
        <input className="input" value={form.contactPhone} onChange={set('contactPhone')} placeholder="+20 100 123 4567" />
      </div>
      <div className="field">
        <label className="field-label">Address</label>
        <input className="input" value={form.address} onChange={set('address')} />
      </div>
      {client && (
        <label className="row" style={{ gap: 8, marginBottom: 16, cursor: 'pointer' }}>
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          <span style={{ fontSize: 13 }}>Active</span>
        </label>
      )}
      <button className="btn btn-primary" type="submit" disabled={busy} style={{ width: '100%' }}>
        {busy ? 'Saving…' : client ? 'Save changes' : 'Create client'}
      </button>
    </form>
  );
}

function SiteForm({ customerId, site, onSaved }: { customerId: string; site?: Site; onSaved: () => void }) {
  const toast = useToast();
  const [form, setForm] = useState({
    name: site?.name ?? '',
    code: site?.code ?? '',
    address: site?.address ?? '',
    contactName: site?.contactName ?? '',
    contactPhone: site?.contactPhone ?? '',
    timezone: site?.timezone ?? '',
    notes: site?.notes ?? '',
  });
  const [active, setActive] = useState(site?.active ?? true);
  const [busy, setBusy] = useState(false);

  const set =
    (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((f) => ({ ...f, [key]: key === 'code' ? e.target.value.toUpperCase() : e.target.value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        code: form.code.trim() || null,
        address: form.address.trim() || null,
        contactName: form.contactName.trim() || null,
        contactPhone: form.contactPhone.trim() || null,
        timezone: form.timezone.trim() || null,
        notes: form.notes.trim() || null,
        active,
      };
      if (site) await api.patch(`/api/sites/${site.id}`, payload);
      else await api.post('/api/sites', { ...payload, customerId });
      onSaved();
    } catch (err) {
      toast(site ? 'Could not update the site' : 'Could not create the site', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <div className="field">
        <label className="field-label">Name</label>
        <input className="input" required autoFocus value={form.name} onChange={set('name')} placeholder="Head office" />
      </div>
      <div className="field">
        <label className="field-label">Code</label>
        <input className="input mono" value={form.code} onChange={set('code')} placeholder="HQ" />
        <span className="field-help">Optional short label, unique within this client.</span>
      </div>
      <div className="field">
        <label className="field-label">Address</label>
        <input className="input" value={form.address} onChange={set('address')} placeholder="14 Corniche El Nil, Cairo" />
      </div>
      <div className="field">
        <label className="field-label">On-site contact</label>
        <input className="input" value={form.contactName} onChange={set('contactName')} />
      </div>
      <div className="field">
        <label className="field-label">On-site phone</label>
        <input className="input" value={form.contactPhone} onChange={set('contactPhone')} placeholder="+20 100 123 4567" />
      </div>
      <div className="field">
        <label className="field-label">Timezone</label>
        <input className="input" value={form.timezone} onChange={set('timezone')} placeholder="Africa/Cairo" />
        <span className="field-help">IANA zone. Used when rendering this site's maintenance windows.</span>
      </div>
      <div className="field">
        <label className="field-label">Notes</label>
        <textarea className="input" rows={3} value={form.notes} onChange={set('notes')} placeholder="Access, keys, escalation…" />
      </div>
      {site && (
        <label className="row" style={{ gap: 8, marginBottom: 16, cursor: 'pointer' }}>
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          <span style={{ fontSize: 13 }}>Active</span>
        </label>
      )}
      <button className="btn btn-primary" type="submit" disabled={busy} style={{ width: '100%' }}>
        {busy ? 'Saving…' : site ? 'Save changes' : 'Create site'}
      </button>
    </form>
  );
}

/**
 * Bulk-attach resources to a site. Offers this client's own unplaced resources
 * first, then anything still unassigned, since claiming an internal device for
 * a client is the common onboarding move.
 */
function AttachResourcesForm({
  site,
  customers,
  onSaved,
}: {
  site: Site;
  customers: Array<{ id: string; name: string }>;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);

  // Every resource the operator can see, not just unplaced ones — a resource
  // already at another one of this client's sites (or even another client's)
  // is a legitimate candidate too, since attaching it here is how you move it.
  // The 500 cap matches the query schema's own max, so this is "all of them"
  // for any deployment this dashboard is actually sized for.
  const all = useFetch(() => api.get<Resource[]>('/api/resources', { limit: 500 }), []);
  const sites = useFetch(() => api.get<SiteRow[]>('/api/sites'), []);

  const customerName = (id: string | null) => (id ? customers.find((c) => c.id === id)?.name ?? id : null);
  const siteName = (id: string | null) => (id ? sites.data?.find((s) => s.id === id)?.name ?? id : null);

  const candidates = useMemo(() => {
    const list = (all.data ?? []).filter((r) => r.siteId !== site.id);
    const needle = search.trim().toLowerCase();
    return needle
      ? list.filter((r) => r.name.toLowerCase().includes(needle) || (accessHostFor(r) ?? '').toLowerCase().includes(needle))
      : list;
  }, [all.data, search, site.id]);

  const toggle = (id: string) =>
    setPicked((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (picked.size === 0) return;

    const movingFromAnotherClient = candidates.some(
      (r) => picked.has(r.id) && r.customerId && r.customerId !== site.customerId,
    );
    if (
      movingFromAnotherClient &&
      !confirm('One or more of these resources belong to a different client. Attaching them here moves them to this client too. Continue?')
    ) {
      return;
    }

    setBusy(true);
    try {
      // Sending only `siteId` keeps this independent of each resource's config
      // completeness — the server derives the customer from the site.
      for (const id of picked) await api.patch(`/api/resources/${id}`, { siteId: site.id });
      onSaved();
    } catch (err) {
      toast('Could not attach every resource', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <p className="subtle" style={{ fontSize: 13, marginBottom: 12 }}>
        Attaching a resource to this site also moves it to this client. A resource already placed elsewhere is shown too —
        picking it relocates it here.
      </p>
      <input
        className="input"
        placeholder="Search resources…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{ marginBottom: 12 }}
      />

      {all.loading || sites.loading ? (
        <SkeletonTable rows={5} cols={2} />
      ) : candidates.length === 0 ? (
        <EmptyState title="Nothing to attach" text="There are no other resources in this system." />
      ) : (
        <div className="col" style={{ gap: 4, maxHeight: 380, overflowY: 'auto', marginBottom: 14 }}>
          {candidates.map((resource) => {
            const elsewhere = resource.siteId
              ? `${siteName(resource.siteId)} · ${customerName(resource.customerId)}`
              : resource.customerId
                ? customerName(resource.customerId)
                : null;
            return (
              <label
                key={resource.id}
                className="card row"
                style={{ padding: '9px 11px', gap: 10, alignItems: 'center', cursor: 'pointer' }}
              >
                <input type="checkbox" checked={picked.has(resource.id)} onChange={() => toggle(resource.id)} />
                <span className="grow truncate">
                  <span style={{ fontWeight: 550, fontSize: 13 }}>
                    <ReachDot health={resource.health} />
                    {resource.name}
                  </span>
                  <span className="subtle" style={{ fontSize: 12 }}>
                    {' '}
                    · {resource.kind}
                    {accessHostFor(resource) ? ` · ${accessHostFor(resource)}` : ''}
                    {elsewhere ? ` · currently at ${elsewhere}` : ' · unplaced'}
                  </span>
                </span>
                <Health health={resource.health} label={false} reason={resource.statusMessage} />
              </label>
            );
          })}
        </div>
      )}

      <button className="btn btn-primary" type="submit" disabled={busy || picked.size === 0} style={{ width: '100%' }}>
        {busy ? 'Attaching…' : picked.size > 0 ? `Attach ${picked.size} resource${picked.size === 1 ? '' : 's'}` : 'Select resources'}
      </button>
    </form>
  );
}
